/**
 * PushNextStepPanel — shared "what's next" surface for Push-Mode modals.
 *
 * Rendered inline AFTER a quick-action modal succeeds (Resolve / Reschedule /
 * Assign). Mirrors the OrderingModal success pattern so operators stay in
 * context and chain through Build Review → Permit → Materials → Crew →
 * Schedule → QA → Closeout without bouncing pages.
 *
 * Three operator paths:
 *   • Continue  → open the next deterministic action (chosen by getJobNextAction)
 *   • Pause     → close cleanly, keep job state as-is
 *   • Hand off  → create a follow-up task on the job and close
 *
 * Pure presentation + a tiny callback contract — no engine logic lives here.
 */
import { CheckCircle, ArrowRight, Users, CalendarDays, FileText, AlertCircle, ShoppingCart, Pause, UserPlus, X, Zap } from "lucide-react";
import type { Job, Issue } from "@/data/types";
import { getJobNextAction } from "@/data/nextActionEngine";
import { useEffect, useMemo, useRef, useState } from "react";
import { PushHandoffPrompt } from "@/components/quick-actions/PushHandoffPrompt";
import { defaultPushHandoffDueDate } from "@/data/pushHandoffEngine";
import { useRole } from "@/data/RoleContext";

export type PushNextStep =
  | { kind: "assign_crew" }
  | { kind: "reschedule" }
  | { kind: "view_permit" }
  | { kind: "resolve_issue" }
  | { kind: "generate_po" }
  | { kind: "navigate"; to: string }
  | { kind: "done" };

const STEP_META: Record<PushNextStep["kind"], { label: string; description: string; icon: React.ElementType }> = {
  assign_crew:   { label: "Assign Crew",     description: "Pick a contractor and lock the install team.",  icon: Users },
  reschedule:    { label: "Reschedule",      description: "Confirm or change the install date.",           icon: CalendarDays },
  view_permit:   { label: "View Permit",     description: "Check permit stage and update status.",         icon: FileText },
  resolve_issue: { label: "Resolve Issue",   description: "Close out an open issue on this job.",          icon: AlertCircle },
  generate_po:   { label: "Generate PO",     description: "Send the material list to the ordering queue.", icon: ShoppingCart },
  navigate:      { label: "Open Next Step",  description: "Continue this workflow on its dedicated page.", icon: ArrowRight },
  done:          { label: "Done",            description: "Close this dialog and return to the queue.",    icon: CheckCircle },
};

interface PushNextStepPanelProps {
  /** Projected job state after the just-completed action (caller supplies the optimistic mutation). */
  job: Job;
  openIssues: Issue[];
  /** One-line headline shown above the picker (e.g. "Issue resolved — what's next?"). */
  headline: string;
  /** Sub-line summary under the headline (e.g. "Status set to Resolved · Note saved"). */
  summary?: string;
  /** Operator picked a chained next step. */
  onContinue: (step: PushNextStep) => void;
  /** Operator wants to stop the chain but keep working later. */
  onPause: () => void;
  /**
   * Operator can't push further — escalate to a tracked follow-up task.
   * Receives the operator-confirmed assignee + due date (ISO). The optional
   * third argument carries canonical routing data (role key / user id) so
   * the engine can persist it for the centralized handoff resolver.
   * If omitted, the legacy free-form `onHandOff` is invoked instead.
   */
  onHandOffConfirmed?: (
    assignee: string,
    dueDateIso: string,
    selection?: import("./PushHandoffPrompt").PushHandoffSelection,
  ) => void;
  /** Legacy escape hatch — kept so older callers still compile while we migrate. */
  onHandOff?: () => void;
  /** Short reason fragment shown in the hand-off prompt for context. */
  handoffReasonFragment?: string;
  /**
   * Phase 4b: When exactly one safe in-modal step is the obvious next move,
   * pre-arm it with a brief cancellable countdown. Default false to preserve
   * the explicit-checkpoint contract; opt in from callers that want momentum.
   */
  autoArm?: boolean;
  /** Countdown seconds before auto-continue fires. Default 3. */
  autoArmSeconds?: number;
}

/**
 * Map an engine SuggestedAction → in-context PushNextStep.
 * IMPORTANT: We deliberately AVOID returning `navigate` here. In Push Mode the
 * primary continuation must keep the operator inside a modal. If the engine
 * pick has no modal mapping, we return null and let the universal fallbacks
 * (assign / reschedule) take over. A `navigate` fallback is only offered when
 * no modal-able step exists at all (handled in the steps memo below).
 */
function deriveNextStep(job: Job, openIssues: Issue[]): PushNextStep | null {
  const next = getJobNextAction(job, openIssues);
  if (!next) return null;
  if (next.executionMode === "modal") {
    if (next.icon === "dispatch" || next.icon === "crew") return { kind: "assign_crew" };
    if (next.icon === "schedule") return { kind: "reschedule" };
    if (next.icon === "issue")    return { kind: "resolve_issue" };
    if (next.icon === "materials") return { kind: "generate_po" };
  }
  if (next.icon === "permit") return { kind: "view_permit" };
  // No modal mapping — do NOT auto-promote `navigate` to the primary chain step.
  return null;
}

/**
 * Last-resort navigate fallback. Kept separate so the primary chain always
 * prefers in-modal continuation.
 *
 * Push Mode is do-mode, not browse-mode. Customer Profile is a CRM browse
 * surface — never a workflow continuation. If the engine's link points to
 * /customers/..., we promote it to the job cockpit (/jobs/:id) instead so
 * the operator lands somewhere they can keep moving the job forward.
 */
function deriveNavigateFallback(job: Job, openIssues: Issue[]): PushNextStep | null {
  const next = getJobNextAction(job, openIssues);
  const link = next?.link;
  if (!link) return null;
  // Suppress browse-mode destinations — redirect to the job cockpit.
  if (link.startsWith("/customers/")) return { kind: "navigate", to: `/jobs/${job.id}` };
  return { kind: "navigate", to: link };
}

export function PushNextStepPanel({ job, openIssues, headline, summary, onContinue, onPause, onHandOff, onHandOffConfirmed, handoffReasonFragment, autoArm = false, autoArmSeconds = 3 }: PushNextStepPanelProps) {
  const { roleInfo } = useRole();
  const [handoffOpen, setHandoffOpen] = useState(false);
  // Build a small ordered list of plausible next steps. Modal steps first;
  // navigate is only added as a last-resort fallback so Push Mode stays in-context.
  const steps = useMemo<PushNextStep[]>(() => {
    const seen = new Set<string>();
    const out: PushNextStep[] = [];
    const push = (s: PushNextStep) => {
      const k = s.kind === "navigate" ? `nav:${s.to}` : s.kind;
      if (!seen.has(k)) { seen.add(k); out.push(s); }
    };
    const engine = deriveNextStep(job, openIssues);
    if (engine) push(engine);
    // Universal in-modal fallbacks — always in-context.
    push({ kind: "assign_crew" });
    push({ kind: "reschedule" });
    // Only add a navigate fallback when the engine had no modal-able step.
    if (!engine) {
      const nav = deriveNavigateFallback(job, openIssues);
      if (nav) push(nav);
    }
    return out.slice(0, 3);
  }, [job, openIssues]);


  const terminal = steps.length === 1 && steps[0].kind === "done";

  // Phase 4b: pre-arm primary step with cancellable countdown.
  // Only fires when caller opted in AND the engine has a deterministic in-modal pick
  // (i.e. the first step came from `deriveNextStep`, not a universal fallback).
  const engineDriven = useMemo(() => deriveNextStep(job, openIssues), [job, openIssues]);
  const armedStep = autoArm && engineDriven && !terminal ? engineDriven : null;
  const [remaining, setRemaining] = useState<number>(autoArmSeconds);
  const [cancelled, setCancelled] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!armedStep || cancelled) return;
    setRemaining(autoArmSeconds);
    firedRef.current = false;
    const tick = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tick);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armedStep?.kind, cancelled]);

  useEffect(() => {
    if (!armedStep || cancelled) return;
    if (remaining !== 0) return;
    if (firedRef.current) return;
    firedRef.current = true;
    onContinue(armedStep);
  }, [armedStep, cancelled, remaining, onContinue]);

  // When the operator confirms hand-off, show a focused prompt instead of
  // the next-step picker. Keeps audit clarity: nothing fires until they
  // pick an assignee + due date and tap "Confirm hand-off".
  if (handoffOpen && onHandOffConfirmed) {
    return (
      <PushHandoffPrompt
        defaultAssignee={roleInfo.label}
        defaultDueDate={defaultPushHandoffDueDate()}
        reasonFragment={handoffReasonFragment ?? `for ${job.customerName} (${job.id})`}
        onCancel={() => setHandoffOpen(false)}
        onConfirm={(assignee, dueIso) => {
          setHandoffOpen(false);
          // Legacy callers without onConfirmStructured still get label + due.
          onHandOffConfirmed(assignee, dueIso);
        }}
        onConfirmStructured={(selection, dueIso) => {
          setHandoffOpen(false);
          onHandOffConfirmed(selection.label, dueIso, selection);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Success headline */}
      <div className="rounded-lg border border-success/30 bg-success/5 p-3 flex items-start gap-3">
        <div className="h-7 w-7 rounded-full bg-success/15 flex items-center justify-center shrink-0">
          <CheckCircle className="h-4 w-4 text-success" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-foreground">{headline}</p>
          {summary && <p className="text-[11px] text-muted-foreground mt-0.5">{summary}</p>}
        </div>
      </div>

      {/* Continue chain */}
      {!terminal && (
        <div className="space-y-2">
          <h3 className="text-[11px] font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="h-3 w-3 text-primary" />
            Continue pushing this job
          </h3>

          {/* Phase 4b: pre-armed countdown notice (cancellable) */}
          {armedStep && !cancelled && remaining > 0 && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5">
              <p className="text-[11px] text-foreground">
                Continuing to <span className="font-semibold">{STEP_META[armedStep.kind].label}</span> in {remaining}s…
              </p>
              <button
                onClick={() => setCancelled(true)}
                className="text-[10px] font-semibold text-primary hover:underline"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="grid gap-2">
            {steps.map((step) => {
              const meta = STEP_META[step.kind];
              const Icon = meta.icon;
              const isArmed = !!armedStep && !cancelled && remaining > 0
                && step.kind === armedStep.kind
                && (step.kind !== "navigate" || (armedStep.kind === "navigate" && step.to === (armedStep as { to: string }).to));
              return (
                <button
                  key={step.kind === "navigate" ? `nav:${step.to}` : step.kind}
                  onClick={() => { setCancelled(true); onContinue(step); }}
                  className={
                    "group flex items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors " +
                    (isArmed
                      ? "border-primary bg-primary/10 hover:bg-primary/15"
                      : "border-border bg-card hover:border-primary/40 hover:bg-primary/5")
                  }
                >
                  <div className={
                    "h-7 w-7 rounded-md flex items-center justify-center shrink-0 transition-colors " +
                    (isArmed
                      ? "bg-primary/20 text-primary"
                      : "bg-muted/40 group-hover:bg-primary/10 text-muted-foreground group-hover:text-primary")
                  }>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-semibold text-foreground">{meta.label}</p>
                    <p className="text-[10px] text-muted-foreground">{meta.description}</p>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Stop-the-chain controls */}
      <div className="border-t border-border pt-3 flex items-center justify-between gap-2">
        <button
          onClick={onPause}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded border bg-muted/40 hover:bg-muted text-foreground transition-colors"
          title="Pause — no follow-up created. Close and pick this back up later."
        >
          <Pause className="h-3 w-3" /> Pause
        </button>
        <button
          onClick={() => {
            // New flow: confirm assignee + due date before creating the task.
            // Falls back to the legacy free-form callback if a caller hasn't migrated.
            if (onHandOffConfirmed) { setHandoffOpen(true); return; }
            if (onHandOff) onHandOff();
          }}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded border border-warning/30 bg-warning/5 text-warning hover:bg-warning/10 transition-colors"
          title="Hand off — creates a follow-up task with an assigned owner and due date."
        >
          <UserPlus className="h-3 w-3" /> Hand off — assign follow-up
        </button>
        {terminal && (
          <button
            onClick={onPause}
            className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded border bg-primary/10 text-primary hover:bg-primary/20 transition-colors ml-auto"
          >
            <X className="h-3 w-3" /> Done
          </button>
        )}
      </div>
    </div>
  );
}
